import { useState } from 'react';
import { toast } from 'sonner';
import { USER_PASSWORD_MIN } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { Button, FieldError, Label } from '@/shared/ui';
import { securityError, useChangePassword } from './account.api';
import { NewPasswordFields, PasswordInput } from './PasswordFields';

/** Change of the own password (current password required). Used on the account page and on the "temporary password" gate. */
export function ChangePasswordForm({ onDone, currentLabel }: { onDone?: () => void; currentLabel?: string }) {
  const { t } = useI18n();
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!current) return setError(t('acct.err.currentRequired'));
    if (password.length < USER_PASSWORD_MIN) return setError(t('acct.err.short', { min: USER_PASSWORD_MIN }));
    if (password !== repeat) return setError(t('acct.mismatch'));
    setError(null);
    change.mutate(
      { currentPassword: current, newPassword: password },
      {
        onSuccess: () => {
          toast.success(t('acct.passwordChanged'));
          setCurrent('');
          setPassword('');
          setRepeat('');
          onDone?.();
        },
        onError: (e) => setError(t(`acct.err.${securityError(e)}`)),
      },
    );
  };

  return (
    <form
      className="space-y-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="acct-current">{currentLabel ?? t('acct.currentPassword')}</Label>
        <PasswordInput id="acct-current" value={current} onChange={setCurrent} autoComplete="current-password" autoFocus />
      </div>
      <NewPasswordFields password={password} repeat={repeat} onPassword={setPassword} onRepeat={setRepeat} />
      <FieldError>{error}</FieldError>
      <Button type="submit" variant="primary" loading={change.isPending}>
        {t('acct.changePassword')}
      </Button>
    </form>
  );
}
